import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import type { Contact } from '@/shared/types/contacts';

export const useContact = (contactId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const queryKey = [contactsEndpoints.contact(contactId ?? '')];

  const { data, status, error } = useQuery({
    queryKey,
    queryFn: () => contactsAPI.getContact(contactId!),
    enabled: !!contactId,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<Contact>) => contactsAPI.updateContact(contactId!, updates),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
    },
    onError: () => {
      toast.error(t('contacts.update_failed'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => contactsAPI.deleteContact(contactId!, false),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey });
    },
    onError: () => {
      toast.error(t('contacts.delete_failed'));
    },
  });

  return {
    contact: data,
    status,
    error,
    updateContact: updateMutation.mutate,
    deleteContact: deleteMutation.mutate,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
